import { FEATURES } from '@/lib/features';
import axiosInstance from './axios';
import { isMockMode, LoginContact, NO_ROUTE, OtpCustomer, OtpUser, RequestOtpResult, toApiError } from './otpAuthService';

/*
 * "Can't sign in? Find your plan" (Figma F1–F5A): connects a customer to a plan Protega already
 * issued, using the order's details and invoice. It never creates cover — anything that doesn't
 * match goes to manual review.
 *
 * API (public, base path /api/auth — requested from the backend, see backend-requests.md):
 *   POST /planRecovery/sendOTP  { mobile_number } | { email }, existing_account?: true
 *        → { data: { expires_in, resend_in } }
 *        → 409 { message } when the contact already has a Protega account; send again with
 *          existing_account: true to verify it and add the plan to that account.
 *   POST /planRecovery/submit   multipart: merchant, order_id, order_date (YYYY-MM-DD), invoice,
 *                               mobile_number | email, otp
 *        → { status: 'linked', token, user, plan: { product_title, image_url, plan_name, order_label } }
 *        → { status: 'review', request_id, submitted_at }   reviewed within 24–48 hours
 *        → { status: 'no_match', request_id, reviewed_at }  the brand has no such order with a plan
 *        Errors: 400 wrong/expired code · 429 too many attempts for this order ID
 *
 * Mock (VITE_OTP_AUTH_MOCK='true'): code 482916 · 98123 45678 or taken@example.com → already has an
 * account · EUME #100245 → linked · order 100999 → no match · anything else → review ·
 * a fourth submission for the same order → too many attempts.
 */

export interface RecoveryRequest {
  brand: { id: string; name: string };
  orderId: string;
  /** YYYY-MM-DD. */
  orderDate: string;
  invoice: File;
  contact: LoginContact;
  otp: string;
}

export type SendRecoveryCodeResult = RequestOtpResult | { status: 'has_account'; message: string };

export interface LinkedPlan {
  productTitle: string;
  imageUrl?: string;
  planName?: string;
  orderLabel?: string;
}

export type RecoveryResult =
  | { status: 'linked'; token: string; customer: OtpCustomer; plan?: LinkedPlan }
  | { status: 'review'; requestId?: string; submittedAt?: string }
  | { status: 'no_match'; requestId?: string; reviewedAt?: string }
  | { status: 'invalid_code'; message: string }
  | { status: 'limited'; message: string }
  | { status: 'failed'; message: string };

const NOT_LIVE = 'Finding your plan with an invoice isn’t available yet. Please chat with us on WhatsApp and we’ll help.';

/** False until the backend ships the recovery endpoints (VITE_PLAN_RECOVERY_LIVE), except in the OTP mock. */
export const planRecoveryAvailable = () => isMockMode() || FEATURES.planRecovery;

const contactFields = (c: LoginContact): Record<string, string> => (c.channel === 'mobile' ? { mobile_number: c.mobile } : { email: c.email });

async function apiSendCode(contact: LoginContact, existingAccount: boolean): Promise<SendRecoveryCodeResult> {
  if (!FEATURES.planRecovery) return { status: 'failed', message: NOT_LIVE };
  try {
    const { data } = await axiosInstance.post<{ data: { resend_in: number; expires_in: number } }>('/api/auth/planRecovery/sendOTP', {
      ...contactFields(contact),
      ...(existingAccount && { existing_account: true }),
    });
    return { status: 'sent', resendInSeconds: data.data.resend_in, expiresInSeconds: data.data.expires_in };
  } catch (err) {
    const { status, message } = toApiError(err);
    if (status === NO_ROUTE) return { status: 'failed', message: NOT_LIVE };
    if (status === 409) return { status: 'has_account', message };
    if (status === 429) {
      const wait = message.match(/wait\s+(\d+)\s*s/i);
      return wait ? { status: 'cooldown', seconds: Number(wait[1]), message } : { status: 'locked', message };
    }
    return { status: 'failed', message };
  }
}

interface SubmitResponse {
  status: 'linked' | 'review' | 'no_match';
  token?: string;
  user?: OtpUser;
  plan?: { product_title: string; image_url?: string; plan_name?: string; order_label?: string };
  request_id?: string;
  submitted_at?: string;
  reviewed_at?: string;
}

async function apiSubmit(req: RecoveryRequest): Promise<RecoveryResult> {
  if (!FEATURES.planRecovery) return { status: 'failed', message: NOT_LIVE };
  const form = new FormData();
  form.append('merchant', req.brand.id);
  form.append('order_id', req.orderId);
  form.append('order_date', req.orderDate);
  form.append('invoice', req.invoice);
  Object.entries(contactFields(req.contact)).forEach(([k, v]) => form.append(k, v));
  form.append('otp', req.otp);
  try {
    const { data } = await axiosInstance.post<SubmitResponse>('/api/auth/planRecovery/submit', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (data.status === 'linked' && data.token && data.user) {
      const plan = data.plan && {
        productTitle: data.plan.product_title,
        imageUrl: data.plan.image_url,
        planName: data.plan.plan_name,
        orderLabel: data.plan.order_label,
      };
      return { status: 'linked', token: data.token, customer: { user: data.user }, plan };
    }
    if (data.status === 'no_match') return { status: 'no_match', requestId: data.request_id, reviewedAt: data.reviewed_at };
    return { status: 'review', requestId: data.request_id, submittedAt: data.submitted_at };
  } catch (err) {
    const { status, message } = toApiError(err);
    if (status === NO_ROUTE) return { status: 'failed', message: NOT_LIVE };
    if (status === 429) return { status: 'limited', message };
    if (status === 400) return { status: 'invalid_code', message };
    return { status: 'failed', message };
  }
}

// ---------- Mock ----------

const MOCK = { code: '482916', takenMobile: '9812345678', takenEmail: 'taken@example.com' };
const mockAttempts = new Map<string, number>();
const wait = (ms = 700) => new Promise((resolve) => setTimeout(resolve, ms));

async function mockSendCode(contact: LoginContact, existingAccount: boolean): Promise<SendRecoveryCodeResult> {
  await wait();
  const taken = contact.channel === 'mobile' ? contact.mobile === MOCK.takenMobile : contact.email.toLowerCase() === MOCK.takenEmail;
  if (taken && !existingAccount) return { status: 'has_account', message: 'This contact already has a Protega account.' };
  return { status: 'sent', resendInSeconds: 60, expiresInSeconds: 300 };
}

async function mockSubmit(req: RecoveryRequest): Promise<RecoveryResult> {
  await wait(1200);
  if (req.otp !== MOCK.code) return { status: 'invalid_code', message: 'Incorrect code. Check it and try again.' };
  const order = req.orderId.replace(/^#/, '');
  const key = `${req.brand.id}:${order}`;
  const attempts = (mockAttempts.get(key) ?? 0) + 1;
  mockAttempts.set(key, attempts);
  if (attempts > 3) return { status: 'limited', message: 'This order has had too many attempts. Please chat with us and we’ll sort it out.' };
  if (order === '100999') return { status: 'no_match', requestId: 'REQ-7Q2M91', reviewedAt: new Date().toISOString() };
  if (req.brand.id === 'eume' && order === '100245') {
    const email = req.contact.channel === 'email' ? req.contact.email : '';
    const phone = req.contact.channel === 'mobile' ? req.contact.mobile : null;
    return {
      status: 'linked',
      token: '',
      customer: { user: { name: 'Priya', email, phone, address: null } },
      plan: { productTitle: 'Classic Cabin (Olive)', planName: '1-Year Accidental Damage', orderLabel: '#100245' },
    };
  }
  return { status: 'review', requestId: 'REQ-7Q2M91', submittedAt: new Date().toISOString() };
}

/**
 * Sends a code to the contact that will become the customer's login. Pass `existingAccount` once
 * they've chosen to add the plan to the account that contact already has.
 */
export const sendRecoveryCode = (contact: LoginContact, existingAccount = false) =>
  isMockMode() ? mockSendCode(contact, existingAccount) : apiSendCode(contact, existingAccount);

/** Verifies the code and asks the backend to match the order and invoice to an issued plan. */
export const submitPlanRecovery = (req: RecoveryRequest) => (isMockMode() ? mockSubmit(req) : apiSubmit(req));
